import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import API from '../../lib/api';
import { getImageUrl } from '../../lib/utils-img';
import { useContent } from '../../lib/content';

export default function Footer() {
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);
  const [quickLinks, setQuickLinks] = useState([
    { label: 'Home', path: '/', visible: true },
    { label: 'Locations', path: '/locations', visible: true },
    { label: 'About', path: '/about', visible: true },
    { label: 'Contact', path: '/contact', visible: true },
    { label: 'Policies', path: '/policies', visible: true },
  ]);
  const { get } = useContent('footer');
  const location = useLocation();

  useEffect(() => {
    API.get('/site-settings').then(r => setSettings(r.data?.site || {})).catch(() => {});
    API.get('/nav').then(r => {
      setLocations(r.data?.locations || []);
      const nav = r.data?.navigation || {};
      if (nav.footer_links && nav.footer_links.length > 0) {
        setQuickLinks(nav.footer_links.filter(i => i.visible !== false));
      }
    }).catch(() => {});
  }, [location.pathname]);

  const email = settings.contact_email || 'info@plushresidence.ca';
  const phone = settings.phone || '+1 (416) 555-0100';
  const address = settings.address || 'Ontario, Canada';
  const description = settings.footer_description || get('description');
  const logoUrl = settings.logo_url || '';
  const logoHeight = settings.logo_height || 40;

  return (
    <footer className="bg-[#0b0b0b] text-white/55" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block" data-testid="footer-logo">
              {logoUrl ? (
                <img src={getImageUrl(logoUrl)} alt="PlushResidence" style={{ height: `${logoHeight}px` }} className="w-auto object-contain" />
              ) : (
                <span className="font-heading text-2xl font-bold text-white tracking-tight">Plush<span className="text-gold">.</span></span>
              )}
            </Link>
            <p className="mt-5 text-sm leading-relaxed max-w-xs text-white/35" data-testid="footer-description">{description}</p>
            <div className="flex items-center gap-4 mt-6">
              <a href={`mailto:${email}`} className="w-10 h-10 rounded-full border border-white/[0.10] flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="footer-email-icon"><Mail className="w-4 h-4" /></a>
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="w-10 h-10 rounded-full border border-white/[0.10] flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="footer-phone-icon"><Phone className="w-4 h-4" /></a>
              <a href="/contact" className="w-10 h-10 rounded-full border border-white/[0.10] flex items-center justify-center hover:border-gold hover:text-gold transition-colors" data-testid="footer-location-icon"><MapPin className="w-4 h-4" /></a>
            </div>
          </div>
          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/30 font-semibold mb-5">{get('quick_links_heading')}</h4>
            <div className="space-y-3">
              {quickLinks.map((l, idx) => (
                <Link key={idx} to={l.path} className="flex items-center gap-1.5 text-sm text-white/45 hover:text-gold transition-colors group" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                  {l.label} <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/30 font-semibold mb-5">{get('locations_heading')}</h4>
            <div className="space-y-3">
              {locations.map(loc => (
                <Link key={loc.slug} to={`/locations/${loc.slug}`} className="flex items-center gap-1.5 text-sm text-white/45 hover:text-gold transition-colors group" data-testid={`footer-loc-${loc.slug}`}>
                  {loc.name} <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            <h4 className="text-[11px] uppercase tracking-[0.25em] text-white/30 font-semibold mb-5">{get('contact_heading')}</h4>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3"><Mail className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span className="text-white/45" data-testid="footer-email">{email}</span></div>
              <div className="flex items-start gap-3"><Phone className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span className="text-white/45" data-testid="footer-phone">{phone}</span></div>
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" /><span className="text-white/45" data-testid="footer-address">{address}</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/25">{get('copyright')}</p>
          <div className="flex gap-8 text-xs text-white/25">
            <Link to="/policies" className="hover:text-gold transition-colors">{get('privacy_text')}</Link>
            <Link to="/policies" className="hover:text-gold transition-colors">{get('terms_text')}</Link>
            <Link to="/policies" className="hover:text-gold transition-colors">{get('cookie_text')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
